/**
 * Run async work with a hard concurrency cap (FIFO).
 * Used to avoid SPA boot request storms against the Railway/Postgres pool.
 */

export type ConcurrencyQueue = {
  run: <T>(fn: () => Promise<T>) => Promise<T>
  /** Queued tasks waiting for a slot (for tests / diagnostics). */
  pendingCount: () => number
  activeCount: () => number
}

export function createConcurrencyQueue(concurrency: number): ConcurrencyQueue {
  const limit = Math.max(1, Math.floor(concurrency))
  let active = 0
  const waiting: Array<() => void> = []

  function pump(): void {
    while (active < limit && waiting.length > 0) {
      active += 1
      const start = waiting.shift()!
      start()
    }
  }

  function run<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      waiting.push(() => {
        Promise.resolve()
          .then(fn)
          .then(resolve, reject)
          .finally(() => {
            active = Math.max(0, active - 1)
            pump()
          })
      })
      pump()
    })
  }

  return {
    run,
    pendingCount: () => waiting.length,
    activeCount: () => active,
  }
}
